import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'

import type { FilterFieldDef, FilterRenderCtx, FilterSurface } from './types'
import { Input } from '@/components/ui/input'
import { useDebounced } from '@/hooks/use-debounced'

interface TextFilterOptions {
    label: () => string
    placeholder: () => string
    /** Chờ bao lâu sau khi ngừng gõ mới đẩy lên URL. */
    debounceMs?: number
}

const controlClass = (surface: FilterSurface) => {
    if (surface === 'sheet') return 'h-11 w-full pl-9'
    // Ô tìm kiếm render một lần cho cả hàng mobile lẫn lưới desktop → phải
    // responsive, không chọn cứng được (R8: vùng chạm 44px trên điện thoại).
    if (surface === 'outside') return 'h-11 w-full pl-9 md:h-9'
    return 'h-9 w-full pl-9'
}

/**
 * Ô nhập tự quản state cục bộ để gõ mượt, debounce rồi mới báo ra ngoài.
 *
 * Tách thành component chứ không gọi hook thẳng trong `render` — `render` là
 * hàm thường, gọi hook trong đó là vi phạm rules-of-hooks và vỡ ngay khi field
 * bị render có điều kiện.
 */
function TextFilterControl({
    id,
    value,
    onChange,
    surface,
    placeholder,
    debounceMs,
}: FilterRenderCtx<string> & { placeholder: string; debounceMs: number }) {
    const [draft, setDraft] = useState(value)
    const debounced = useDebounced(draft, debounceMs)

    // Gõ xong → đẩy ra ngoài.
    // Cố ý chỉ phụ thuộc `debounced`: thêm `value`/`onChange` vào deps sẽ bắn
    // lại ngay khi URL vừa cập nhật, thành vòng lặp đẩy-đồng bộ vô tận.
    useEffect(() => {
        if (debounced !== value) onChange(debounced)
    }, [debounced])

    // URL đổi từ nơi khác (back/forward, xoá lọc, deep-link) → đồng bộ ngược.
    useEffect(() => {
        setDraft(value)
    }, [value])

    return (
        <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
                id={id}
                placeholder={placeholder}
                className={controlClass(surface)}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
            />
        </div>
    )
}

/**
 * Ô tìm kiếm. LUÔN nằm ngoài sheet (R2) vì đây là thao tác dùng nhiều nhất,
 * giấu sau một nút là thêm một chạm cho việc hay làm nhất.
 *
 * Cặp effect debounce + đồng bộ ngược ở trên hiện đang lặp ở 26 file, mỗi chỗ
 * ~14 dòng — và chính nó đẻ ra bug "bấm Áp dụng xoá mất từ khoá vừa gõ".
 */
export function textFilter({
    label,
    placeholder,
    debounceMs = 300,
}: TextFilterOptions): FilterFieldDef<string> {
    return {
        defaultValue: '',
        label,
        placement: 'outside',
        parse: (raw) => (typeof raw === 'string' && raw ? raw : undefined),
        chipLabel: (value) => `${label()}: ${value}`,
        render: (ctx) => (
            <TextFilterControl
                {...ctx}
                placeholder={placeholder()}
                debounceMs={debounceMs}
            />
        ),
    }
}
